<template>
  <base-modal
    :title="'Global variables'"
    :width="640"
    @close-modal="onCloseModal"
  >
    <div ref="wrapper" class="wrapper">
      <div v-if="canEdit && variables.length > 0" class="labels">
        <div class="name">Name</div>
        <div class="type">Type</div>
        <div class="initialValue">Initial value</div>
      </div>
      <!-- Special focus guard element to cycle tabindex inside modal -->
      <div
        ref="firstGuard"
        class="focusguard"
        tabindex="0"
        @focus="onFocusFirstGuard"
      />
      <draggable
        v-if="variables.length"
        v-bind="dragOptions"
        ref="varContainer"
        :list="variables"
        class="draggable-container"
        handle=".handle"
        item-key="id"
        @start="drag = true"
        @end="onEndDrag"
      >
        <template #item="{ index, element }">
          <global-var-row :id="element.id" :ref="'variable_' + index" />
        </template>
      </draggable>
      <div
        v-if="canEdit && variables.length > 0"
        class="button"
        @click="onClickAdd"
      >
        New variable
      </div>
      <div v-if="canEdit && variables.length === 0" class="buttonarea">
        <button class="addfield" @click="onClickAdd">
          <svg-icon :width="'18'" :height="'18'" icon="app-icons/create" />
          New global variable
        </button>
      </div>
    </div>
    <!-- Special focus guard element to cycle tabindex inside modal -->
    <div
      ref="lastGuard"
      class="focusguard"
      tabindex="0"
      @focus="onFocusLastGuard"
    />
  </base-modal>
</template>

<script>
import draggable from 'vuedraggable';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import { useProtect } from '@/composables/useProtect.js';
import { SYS_PERMISSIONS } from '@/constants.js';
import GlobalVarRow from './GlobalVarRow.vue';
import BaseModal from '../BaseModal.vue';
import SvgIcon from '../../all/SvgIcon.vue';

draggable.compatConfig = { MODE: 3 };

export default {
  components: {
    BaseModal,
    draggable,
    GlobalVarRow,
    SvgIcon,
  },

  setup() {
    const { hasPermission: canEdit } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EDIT],
      roles: ['editor'],
    });

    return {
      canEdit,
    };
  },

  computed: {
    ...mapGetters('application/modals', ['getGlobalVarsModal']),
    ...mapGetters('project/data/variables', [
      'getContainedNodes',
      'getRootContainer',
    ]),

    /**
     * The root container id
     * @returns {String}
     */
    rootId() {
      return this.getRootContainer.id;
    },

    /**
     * The variable objects for this project
     * @returns {Array}   The variable objects in an array
     */
    variables() {
      return this.getContainedNodes(this.rootId, 'variables', 'children');
    },

    /**
     * Return the draggable options
     * @returns {Object}
     */
    dragOptions() {
      return {
        animation: 200,
        group: 'globalvars',
        disabled: !this.canEdit,
        ghostClass: 'ghost',
      };
    },
  },
  mounted() {
    // Give focus to the modal
    this.$refs.firstGuard.focus();
    // console.log('MODAL:', this.$refs.lastGuard.focus());
  },
  methods: {
    ...mapActions('application/modals', ['setGlobalVarsModal']),
    ...mapActions('project/data/variables', ['move']),
    ...mapActions('project/data/variables', { createVariable: 'customCreate' }),

    /**
     * Event handler: The user clicks outside the share modal, or on the close button
     */
    onCloseModal() {
      this.setGlobalVarsModal({ value: false });
    },

    /**
     * Event handler: A draggable item has been dragged to a new position
     */
    onEndDrag(event) {
      const { oldIndex, newIndex } = event;
      this.drag = false;
      const id = this.getRootContainer.children[oldIndex];
      this.move({
        id,
        tId: this.rootId,
        tIndex: newIndex,
        adjust: false,
        apply: true,
      });
    },

    /**
     * Event handler: The user clicks on the add variable button
     */
    onClickAdd() {
      // Create the new variable at the end of the root container
      this.createVariable({ props: {}, tIndex: '-', apply: true });

      // Focus the new var name
      this.$nextTick(() => {
        this.$refs[
          `variable_${this.$refs.varContainer.list.length - 1}`
        ].$refs.name.focus();
      });
    },

    /**
     * Event handler: the user presses Shift+Tab while on 1st tabbable element (name field).
     * In that case focus is passed to the guard and the guard passes the focus to the last tabbable element.
     * @param {FocusEvent} event
     */
    onFocusFirstGuard(event) {
      if (!event.relatedTarget) return;
      console.log('FIRSTGUARD', event.relatedTarget);
      if (this.$refs.varContainer.list.length > 0) {
        this.$refs[
          `variable_${this.$refs.varContainer.list.length - 1}`
        ].$refs.initialValue.focus();
      }
    },

    /**
     * Event handler: the user presses Tab while on the last tabbable element.
     */
    onFocusLastGuard(event) {
      console.log('LASTGUARD', event.relatedTarget);
      this.$refs.variable_0.$refs.name.focus();
    },
  },
};
</script>

<style scoped lang="scss">
.wrapper {
  padding: 20px 0;
  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    flex-flow: column;
  }
  .draggable-container {
    display: flex;
    flex-flow: column;
  }
  .labels {
    display: flex;
    color: #828585;
    font-size: 14px;
    padding: 0 30px;
    margin: 0 0 20px 0;
    > div {
      margin: 0 10px 0 0;
      padding-right: 20px;
      &:last-child {
        margin: 0;
      }
    }
    .name {
      flex: 3;
    }
    .type {
      flex: 1;
    }
    .initialValue,
    .booleanValue {
      flex: 2;
    }
  }
}

.modal .button {
  padding: 10px 18px;
  border-radius: 3px;
  width: 100%;
  color: #fff;
  font-size: 13px;
  background: #db841e;
  cursor: pointer;
  width: auto;
  text-align: center;
  margin: 10px 30px;
  display: inline-block;
}
.buttonarea {
  padding: 0 30px;
  margin: 0 0 10px 0;
  button.addfield {
    padding: 14px 16px;
    border-radius: 3px;
    width: 100%;
    display: flex;
    align-items: center;
    border: none;
    color: #bcbcbc;
    background: transparent;
    cursor: pointer;
    font-family: 'Open sans';
    font-weight: 600;
    font-size: 14px;
    text-align: left;
    opacity: 0.5;
    .svg-icon-container {
      margin: 0 10px 0 0;
    }
    &:hover {
      background: #383f40;
      opacity: 1;
    }
  }
}
</style>
